import { Box, styled } from '@mui/material';
import { ReactNode, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import {
  CollaborateNativeEventsHandler,
  MessagesTypes,
  useCollaborateBridgeListener,
} from 'src/services/CollaborateNativeBridge';

import { useWorkspaceInfo } from '@modules/workspace/providers/WorkspaceInfo';
import cn from 'classnames';
import { differenceInDays } from 'date-fns';
import { ErrorBoundary } from 'react-error-boundary';
import { BillingStatus } from 'src/@types/billing';
import { useAuthContext } from 'src/auth/useAuthContext.js';
import { UncaughtError } from 'src/components/base/UncaughtError';
import { FileUploadProgress } from 'src/features/FileUploadProgress';
import { GatheringSpacesBar } from 'src/features/RTCBar';
import { useSubscription } from 'src/hooks/useSubscription';
import { MobileBar } from 'src/layouts/DashboardLayout/MobileBar';
import { useNavbarContext } from 'src/provider/NavbarContext';
import { SocketEventsHandler } from 'src/provider/SocketEventsHandler.js';
import { useSocketEvent } from 'src/socket';
import { onUserRefresh } from 'src/socket/listeners/user';
import { useIsAppCollaborate } from 'src/state';
import { ActionsBar } from '../../features/ActionsBar';
import { useBreakpoints } from '../../hooks/useBreakpoints';
import { AppGrid } from '../shared/AppGrid';
import Header from '../shared/Header';
import { LayoutMainContainer } from '../shared/LayoutMainContainer';
import { ResponsiveVerticalNav } from '../shared/ResponsiveVerticalNav';
import { FreePlanExceededAlert } from './FreePlanExceededAlert/FreePlanExceededAlert';
import { GlobalAlert } from './GlobalAlert/GlobalAlert';
import { useGlobalAlert } from './GlobalAlert/useGlobalAlert';
import { TrialEndingAlert } from './TrialEndingAlert';
import { UnverifiedAccountLocked } from './UnverifiedAccountLocked.jsx';
import { VerifyAccountAlert } from './VerifyAccountAlert/VerifyAccountAlert';
import { WorkspaceLocked } from './WorkspaceLocked';

const Content = styled(Box)(() => ({
  display: 'flex',
  flexGrow: 1,
  width: '100%',
  gridArea: 'content',
  position: 'relative',
  overflow: 'hidden',
}));

const StyledHeaderBox = styled(Box)(() => ({
  gridArea: 'header',
  '&.isAppCollaborate': { display: 'none' },
}));

const NavHoverAnchor = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: theme.spacing(4),
  height: '100%',
  zIndex: 3,
}));

type Props = {
  children: ReactNode;
};

export function DashboardLayout({ children }: Props) {
  const { workspace: _workspace } = useWorkspaceInfo();

  const { user, refresh } = useAuthContext();
  const { hiddenNavBar, openNavBar, setOpenNavBar, toggleNavBar } =
    useNavbarContext();
  const isAppCollaborate = useIsAppCollaborate();

  const { freePlanMembersExceeded, workspaceLocked, subscription } =
    useSubscription();

  const { hasAlert } = useGlobalAlert();

  const { isDesktop, isMobile } = useBreakpoints();

  const handleOpen = () => {
    setOpenNavBar(true);
  };

  const handleClose = () => {
    setOpenNavBar(false);
  };

  useSocketEvent(
    onUserRefresh(async () => {
      refresh();
    }),
  );

  const accountLockedByUnverifiedEmail = useMemo(() => {
    if (!user) return false;

    if (user?.verified.email) return false;

    const accountAge = differenceInDays(new Date(), new Date(user?.createdAt));

    return accountAge > 7;
  }, [user]);

  const othersAlerts = useMemo(() => {
    if (!isDesktop) return;
    if (freePlanMembersExceeded) return <FreePlanExceededAlert />;
    if (!user?.verified.email) return <VerifyAccountAlert />;
  }, [isDesktop, user, freePlanMembersExceeded]);

  useCollaborateBridgeListener(MessagesTypes.COLLABORATE_OPEN_MENU, () => {
    toggleNavBar();
  });

  return (
    <>
      <AppGrid
        className={cn({
          isAppCollaborate,
          hasNotification: isDesktop && hasAlert,
        })}
      >
        {othersAlerts}
        {isDesktop && <GlobalAlert />}
        <StyledHeaderBox className={cn({ isAppCollaborate })}>
          <Header />
        </StyledHeaderBox>
        <Content>
          {accountLockedByUnverifiedEmail ? (
            <UnverifiedAccountLocked />
          ) : workspaceLocked ? (
            <WorkspaceLocked />
          ) : (
            <>
              {children && (
                <>
                  {hiddenNavBar && <NavHoverAnchor onMouseEnter={handleOpen} />}
                  <ResponsiveVerticalNav
                    openNav={openNavBar}
                    onCloseNav={handleClose}
                  >
                    <ErrorBoundary FallbackComponent={UncaughtError}>
                      {children}
                    </ErrorBoundary>
                    <FileUploadProgress />
                    <GatheringSpacesBar />
                  </ResponsiveVerticalNav>
                </>
              )}

              <LayoutMainContainer>
                {subscription?.status === BillingStatus.TRIAL_WILL_END &&
                !subscription.cards.length ? (
                  <TrialEndingAlert endAt={subscription?.trial_end} />
                ) : null}
                <Outlet />
              </LayoutMainContainer>
              <ActionsBar />
            </>
          )}
        </Content>
        {isMobile && !isAppCollaborate && (
          <Box sx={{ gridArea: 'mobileBar' }}>
            <MobileBar onOpenNav={handleOpen} />
          </Box>
        )}
        <SocketEventsHandler />
        <CollaborateNativeEventsHandler />
      </AppGrid>
    </>
  );
}
